import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/interview/api";
import { Button } from "../../components/ui/button";
import { Logo, Overline } from "../../components/himt/Primitives";
import { fmtIST } from "../../lib/interview/time";
import { toast } from "sonner";
import { ShieldCheck, Anchor, AlertTriangle, Clock, Mic, Video, MonitorSmartphone, Smartphone, Chrome, ListChecks } from "lucide-react";
import { detectBrowser, BROWSER_BLOCK_COPY } from "../../lib/interview/browser";

export default function StudentGate() {
  const { token, code } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [resolvedToken, setResolvedToken] = useState(token);
  const [browser] = useState(() => detectBrowser());

  useEffect(() => {
    (async () => {
      try {
        let jwt = token;
        // If we have a short code instead of a JWT, resolve it first
        if (code) {
          const { data } = await api.get(`/exam/s/${code}`);
          jwt = data.jwt;
          setResolvedToken(jwt);
          // Redirect to the JWT-based URL
          nav(`/exam/${jwt}`, { replace: true });
          return;
        }
        if (!jwt) {
          setError("No invite token provided");
          return;
        }
        const { data } = await api.post("/student/verify", { token: jwt });
        setInfo(data);
        localStorage.setItem("himt_student_token", jwt);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invalid or expired test link.");
      }
    })();
  }, [token, code]);

  const activeToken = resolvedToken || token;

  const begin = () => {
    if (info?.schedule && !info.schedule.is_open) {
      if (info.schedule.not_yet) {
        toast.error("This test is not open yet. Please check the scheduled start time.");
      } else if (info.schedule.expired) {
        toast.error("This test has closed.");
      }
      return;
    }
    if (info.attempt_status === "in_progress") {
      nav(`/exam/${activeToken}/live`);
    } else if (info.attempt_status === "submitted" || info.attempt_status === "graded") {
      nav(`/exam/${activeToken}/complete`);
    } else {
      nav(`/exam/${activeToken}/env`);
    }
  };

  // Browser gate — block Safari / Firefox / mobile / unsupported browsers
  // BEFORE the candidate reaches the pre-flight. Run FIRST (before the
  // invalid-token path) so Safari users always see the friendly install
  // screen, even if the token check failed or the backend was slow.
  if (!browser.isSupported) {
    const label = browser.reason === "mobile" ? "Mobile device detected"
      : browser.reason === "safari" ? "Safari is not supported"
      : browser.reason === "firefox" ? "Firefox is not supported"
      : BROWSER_BLOCK_COPY.title;
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6" data-testid="browser-block-screen">
        <div className="max-w-xl w-full border-2 border-red-500 bg-red-50 rounded-sm p-8 sm:p-10">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 shrink-0 rounded-sm bg-red-600 text-white grid place-items-center">
              <AlertTriangle size={24} />
            </div>
            <div className="flex-1">
              <Overline className="text-red-700">Unsupported browser</Overline>
              <h1 className="font-display font-black tracking-tight text-2xl sm:text-3xl text-[#0A2540] mt-1" data-testid="browser-block-title">
                {label}
              </h1>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                {BROWSER_BLOCK_COPY.message}
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {BROWSER_BLOCK_COPY.actions.map((a) => (
                  <a
                    key={a.href}
                    href={a.href} target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-2 bg-[#0A2540] text-white rounded-sm py-3 text-sm font-bold hover:bg-[#0A2540]/90"
                    data-testid={`browser-install-${a.href.includes('chrome') ? 'chrome' : 'edge'}-link`}
                  >
                    <Chrome size={16} /> {a.label}
                  </a>
                ))}
              </div>
              <div className="mt-4 bg-white border border-red-200 rounded-sm p-3 text-xs font-mono text-slate-600">
                {BROWSER_BLOCK_COPY.help}
              </div>
              <div className="mt-3 text-[10px] font-mono text-slate-400 tracking-wider uppercase">
                Detected: {browser.name} · {browser.isMobile ? "mobile" : "desktop"}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6">
        <div className="max-w-md text-center border border-red-200 bg-red-50 rounded-sm p-8">
          <AlertTriangle size={40} className="mx-auto text-red-600" />
          <h1 className="font-display font-bold tracking-tight text-2xl text-[#0A2540] mt-4">Access Denied</h1>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!info) return <div className="min-h-screen grid place-items-center text-slate-500 text-sm font-mono">Verifying invitation…</div>;

  const scheduleClosed = info.schedule && !info.schedule.is_open;
  const isResume = info.attempt_status === "in_progress";
  const ctaLabel = info.schedule?.not_yet
    ? "Not yet open"
    : info.schedule?.expired
      ? "Test closed"
      : isResume
        ? "Welcome back"
        : "Ready?";
  const ctaSub = info.schedule?.not_yet
    ? `Opens ${info.test.scheduled_start ? fmtIST(info.test.scheduled_start) : "soon"}`
    : info.schedule?.expired
      ? `Closed ${info.test.scheduled_end ? fmtIST(info.test.scheduled_end) : ""}`
      : isResume
        ? "Your test is in progress. Tap Resume to pick up where you left off."
        : "Next: pre-test environment & identity check.";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-14 flex items-center">
          <Logo />
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-6 lg:px-10 py-10 lg:py-12">
        {/* Greeting — compact, balanced */}
        <Overline>Welcome, candidate</Overline>
        <h1
          className="font-sans font-semibold tracking-tight text-[#0A2540] text-3xl sm:text-4xl mt-2 leading-tight"
          data-testid="student-name-heading"
        >
          {info.student_name}
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base">
          You have been invited to attempt{" "}
          <span className="font-semibold text-[#0A2540]">{info.test.title}</span>. Please
          review the rules carefully before you begin.
        </p>

        {/* Test overview + rules — equal-height cards, consistent styling */}
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <section className="border border-slate-200 rounded-sm p-5 bg-white">
            <div className="flex items-center justify-between">
              <Overline>Test overview</Overline>
              <Clock size={13} className="text-slate-400" />
            </div>
            <dl className="mt-4 space-y-3 text-sm text-slate-700">
              {info.test.exam_mode === "combined" && (
                <div className="flex justify-between items-baseline" data-testid="overview-exam-type">
                  <dt>Exam type</dt>
                  <dd className="font-mono font-bold text-[#2563EB]">MCQ + Oral</dd>
                </div>
              )}
              {info.test.exam_mode === "combined" && (
                <>
                  <div className="flex justify-between items-baseline" data-testid="overview-mcq-count">
                    <dt>MCQ questions</dt>
                    <dd className="font-mono font-bold text-[#0A2540]">{info.test.mcq_question_count}</dd>
                  </div>
                  <div className="flex justify-between items-baseline" data-testid="overview-mcq-duration">
                    <dt>MCQ time limit</dt>
                    <dd className="font-mono font-bold text-[#0A2540]">{info.test.mcq_duration_minutes} min</dd>
                  </div>
                </>
              )}
              <div className="flex justify-between items-baseline">
                <dt>{info.test.exam_mode === "combined" ? "Oral questions" : "Questions"}</dt>
                <dd className="font-mono font-bold text-[#0A2540]">{info.test.question_count}</dd>
              </div>
              <div className="flex justify-between items-baseline">
                <dt>Start speaking within</dt>
                <dd className="font-mono font-bold text-[#0A2540]">{info.test.ttr_seconds}s</dd>
              </div>
              <div className="flex justify-between items-baseline">
                <dt>Max answer duration</dt>
                <dd className="font-mono font-bold text-[#0A2540]">{info.test.answer_duration_seconds}s</dd>
              </div>
              <div className="flex justify-between items-baseline">
                <dt>{info.test.exam_mode === "combined" ? "Oral duration" : "Total duration"}</dt>
                <dd className="font-mono font-bold text-[#0A2540]">{info.test.total_duration_minutes} min</dd>
              </div>
            </dl>
          </section>

          <section className="border border-slate-200 rounded-sm p-5 bg-slate-50">
            <Overline>Rules of examination</Overline>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
              {info.test.exam_mode === "combined" && (
                <li className="flex gap-2" data-testid="rule-combined">
                  <ListChecks size={13} className="mt-1 text-[#2563EB] shrink-0" />
                  <span>This exam has a <b>written MCQ section</b> and a spoken <b>Oral section</b>{info.test.phase_order === "oral_first" ? " (oral first, then MCQ)" : " (MCQ first, then oral)"}. Both are proctored continuously.</span>
                </li>
              )}
              <li className="flex gap-2">
                <Mic size={13} className="mt-1 text-slate-500 shrink-0" />
                <span>Begin speaking within <b>{info.test.ttr_seconds} seconds</b> or the question auto-skips.</span>
              </li>
              <li className="flex gap-2">
                <Video size={13} className="mt-1 text-slate-500 shrink-0" />
                <span>Your webcam, microphone and screen are <b>recorded</b> throughout.</span>
              </li>
              <li className="flex gap-2">
                <Smartphone size={13} className="mt-1 text-slate-500 shrink-0" />
                <span>A <b>smartphone is mandatory</b> for the 360° room scan (QR-code handoff, 10 seconds).</span>
              </li>
              <li className="flex gap-2">
                <MonitorSmartphone size={13} className="mt-1 text-slate-500 shrink-0" />
                <span>Do not switch tabs or leave the browser — any exit is flagged.</span>
              </li>
              <li className="flex gap-2">
                <ShieldCheck size={13} className="mt-1 text-slate-500 shrink-0" />
                <span>No other voice should be audible in the room while you answer.</span>
              </li>
            </ul>
          </section>
        </div>

        {/* Mobile-mandatory banner — reinforces the smartphone requirement so
            candidates aren't blindsided when the 360° scan step only offers
            the Phone (QR) option. */}
        <div
          className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 rounded-sm"
          data-testid="mobile-required-banner"
        >
          <Smartphone size={20} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-800">
            <b className="text-amber-900">Smartphone required.</b> Keep a phone with a working camera + internet nearby before you begin. You'll scan a QR code to record a 10-second 360° view of your room — this step cannot be skipped or substituted.
          </div>
        </div>

        {/* CTA strip — tighter padding, consistent with card spacing */}
        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 sm:p-6 bg-[#0A2540] rounded-sm text-white">
          <div className="flex items-center gap-3 sm:gap-4">
            <Anchor size={22} />
            <div>
              <div className="font-display font-black text-lg sm:text-xl tracking-tight">{ctaLabel}</div>
              <div className="text-xs sm:text-sm text-white/70">{ctaSub}</div>
            </div>
          </div>
          <Button
            onClick={begin}
            disabled={scheduleClosed}
            className="bg-white text-[#0A2540] hover:bg-white/90 rounded-sm px-6 py-5 text-sm font-bold disabled:opacity-50 w-full sm:w-auto"
            data-testid="begin-exam-btn"
          >
            <ShieldCheck size={16} className="mr-2" /> {isResume ? "Resume Examination" : "Begin Environment Check"}
          </Button>
        </div>
      </main>
    </div>
  );
}
